// Add forms for all three input types: Accrual, Commitment, PlannedSpend.
// Each form is self-contained: owns local state, calls its mutation,
// and delegates overlap resolution to OverlapWarning.

import { useState, useCallback, useMemo } from "react";
import { AlertCircle, Loader2 } from "lucide-react";
import {
  useCreateForecastAccrual,
  useCreateForecastCommitment,
  useCreateForecastPlannedSpend,
} from "@workspace/api-client-react";
import type {
  ForecastInputRecord,
  ForecastInputOverlap,
  ForecastCommitmentInputCommitmentType,
} from "@workspace/api-client-react";
import { ScheduleBuilder, OverlapWarning } from "./schedule-builder";
import { formatPeriod, economicKey } from "./helpers";
import type { ForecastContext, ScheduleEntry, ScheduleType } from "./types";

// ── Shared context strip ──────────────────────────────────────────────────────

function FormContextStrip({ ctx }: { ctx: ForecastContext }) {
  return (
    <div className="fi-form-context" aria-label="Inherited forecast context">
      <span>FY{ctx.fiscalYear}</span>
      <span>{formatPeriod(ctx.throughPeriod, ctx.fiscalYear)}</span>
      <span>{ctx.departmentCode}</span>
      <span className="fi-source-badge">Manual Forecast Input</span>
    </div>
  );
}

// ── Shared form error ─────────────────────────────────────────────────────────

function SaveError() {
  return (
    <div className="source-page-error" role="alert">
      <AlertCircle size={13} aria-hidden="true" />
      <span>Save failed. Please check your inputs and try again.</span>
    </div>
  );
}

// ── Shared form actions ───────────────────────────────────────────────────────

interface FormActionsProps {
  isPending: boolean;
  canSave: boolean;
  hasRunId: boolean;
  prefix: string;
  onCancel: () => void;
  onSaveOnly: () => void;
  onSaveRefresh: () => void;
}

function FormActions({
  isPending, canSave, hasRunId, prefix,
  onCancel, onSaveOnly, onSaveRefresh,
}: FormActionsProps) {
  return (
    <div className="fi-form-actions">
      <button
        className="tl-btn"
        onClick={onCancel}
        aria-label="Cancel and close this form"
        data-testid={`fi-${prefix}-cancel`}
      >
        Cancel
      </button>
      <button
        className="tl-btn"
        onClick={onSaveOnly}
        disabled={isPending || !canSave}
        aria-label="Save this input without refreshing the forecast"
        data-testid={`fi-${prefix}-save-only`}
      >
        {isPending ? <Loader2 size={12} className="animate-spin" aria-hidden="true" /> : null} Save Only
      </button>
      <button
        className="tl-btn primary"
        onClick={onSaveRefresh}
        disabled={isPending || !canSave || !hasRunId}
        aria-label="Save this input and refresh the forecast"
        data-testid={`fi-${prefix}-save-refresh`}
      >
        Save &amp; Refresh Forecast
      </button>
    </div>
  );
}

// ── Add Accrual ───────────────────────────────────────────────────────────────

interface AddAccrualFormProps {
  ctx: ForecastContext;
  onSaved: (record: ForecastInputRecord, refresh: boolean) => void;
  onCancel: () => void;
}

export function AddAccrualForm({ ctx, onSaved, onCancel }: AddAccrualFormProps) {
  const [period, setPeriod] = useState(String(ctx.throughPeriod));
  const [glAccount, setGlAccount] = useState("");
  const [vendor, setVendor] = useState("");
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [owner, setOwner] = useState("");
  const [note, setNote] = useState("");
  const [sourceRef, setSourceRef] = useState("");
  const [overlaps, setOverlaps] = useState<ForecastInputRecord[]>([]);
  const [pendingSaveAnyway, setPendingSaveAnyway] = useState(false);

  const createMutation = useCreateForecastAccrual();

  const handleSubmit = useCallback((saveAnyway = false, withRefresh = false) => {
    if (!description || !amount || !glAccount) return;
    createMutation.mutate({
      data: {
        companyId: ctx.companyId,
        fiscalYear: ctx.fiscalYear,
        departmentCode: ctx.departmentCode,
        glAccount,
        economicKey: economicKey(glAccount, vendor, ctx.fiscalYear, period),
        amount: Number(amount),
        currency: "USD",
        sourceReference: sourceRef || `manual-accrual-${Date.now()}`,
        period: Number(period),
        description,
        vendor: vendor || undefined,
        owner: owner || undefined,
        note: note || undefined,
        saveAnyway,
        actor: "ER",
      },
    }, {
      onSuccess: (record) => {
        setOverlaps([]);
        setPendingSaveAnyway(false);
        onSaved(record, withRefresh);
      },
      onError: (err: unknown) => {
        const overlap = err as ForecastInputOverlap;
        if (overlap?.code === "POSSIBLE_OVERLAP") {
          setOverlaps(overlap.overlaps ?? []);
          setPendingSaveAnyway(withRefresh);
        }
      },
    });
  }, [ctx, description, amount, glAccount, vendor, period, sourceRef, owner, note, createMutation, onSaved]);

  const periodOptions = useMemo(() => {
    const opts: { fy: number; period: number }[] = [];
    for (let p = 1; p <= 12; p++) opts.push({ fy: ctx.fiscalYear, period: p });
    return opts;
  }, [ctx.fiscalYear]);

  if (overlaps.length > 0) {
    return (
      <OverlapWarning
        overlaps={overlaps}
        onCancel={() => setOverlaps([])}
        isPending={createMutation.isPending}
        onSaveAnyway={() => handleSubmit(true, pendingSaveAnyway)}
      />
    );
  }

  const canSave = !!(description && amount && glAccount);

  return (
    <div className="fi-form" data-testid="fi-add-accrual-form">
      <FormContextStrip ctx={ctx} />

      <div className="fi-field">
        <label htmlFor="fi-accrual-period">Period <span className="fi-required" aria-label="required">*</span></label>
        <select
          id="fi-accrual-period"
          value={period}
          onChange={e => setPeriod(e.target.value)}
          data-testid="fi-accrual-period"
        >
          {periodOptions.map(({ fy, period: p }) => (
            <option key={p} value={String(p)}>{formatPeriod(p, fy)}</option>
          ))}
        </select>
        <span className="fi-help">Accrual belongs to the period the cost was incurred, not when the invoice posts.</span>
      </div>

      <div className="fi-field">
        <label htmlFor="fi-accrual-gl">GL Account <span className="fi-required" aria-label="required">*</span></label>
        <input
          id="fi-accrual-gl"
          type="text"
          value={glAccount}
          onChange={e => setGlAccount(e.target.value)}
          placeholder="e.g. 45631 · IT Software Purchases"
          data-testid="fi-accrual-gl"
        />
      </div>

      <div className="fi-field">
        <label htmlFor="fi-accrual-vendor">Vendor</label>
        <input
          id="fi-accrual-vendor"
          type="text"
          value={vendor}
          onChange={e => setVendor(e.target.value)}
          placeholder="e.g. Microsoft"
          data-testid="fi-accrual-vendor"
        />
      </div>

      <div className="fi-field">
        <label htmlFor="fi-accrual-description">Description <span className="fi-required" aria-label="required">*</span></label>
        <input
          id="fi-accrual-description"
          type="text"
          value={description}
          onChange={e => setDescription(e.target.value)}
          placeholder="e.g. July Azure usage estimate"
          data-testid="fi-accrual-description"
        />
      </div>

      <div className="fi-field">
        <label htmlFor="fi-accrual-amount">Amount ($) <span className="fi-required" aria-label="required">*</span></label>
        <input
          id="fi-accrual-amount"
          type="number"
          min={0}
          value={amount}
          onChange={e => setAmount(e.target.value)}
          placeholder="0"
          data-testid="fi-accrual-amount"
        />
      </div>

      <div className="fi-field">
        <label htmlFor="fi-accrual-owner">Owner</label>
        <input
          id="fi-accrual-owner"
          type="text"
          value={owner}
          onChange={e => setOwner(e.target.value)}
          placeholder="Optional"
          data-testid="fi-accrual-owner"
        />
      </div>

      <div className="fi-field">
        <label htmlFor="fi-accrual-note">Source / Note</label>
        <input
          id="fi-accrual-note"
          type="text"
          value={note}
          onChange={e => setNote(e.target.value)}
          placeholder="e.g. Cloud estimate pending invoice"
          data-testid="fi-accrual-note"
        />
      </div>

      <div className="fi-field">
        <label htmlFor="fi-accrual-source-ref">Invoice / PO Reference</label>
        <input
          id="fi-accrual-source-ref"
          type="text"
          value={sourceRef}
          onChange={e => setSourceRef(e.target.value)}
          placeholder="Optional"
          data-testid="fi-accrual-source-ref"
        />
      </div>

      {createMutation.isError && !(createMutation.error as unknown as ForecastInputOverlap)?.code && <SaveError />}

      <FormActions
        isPending={createMutation.isPending}
        canSave={canSave}
        hasRunId={!!ctx.runId}
        prefix="accrual"
        onCancel={onCancel}
        onSaveOnly={() => handleSubmit(false, false)}
        onSaveRefresh={() => handleSubmit(false, true)}
      />
    </div>
  );
}

// ── Add Commitment ────────────────────────────────────────────────────────────

const COMMITMENT_TYPES: { value: ForecastCommitmentInputCommitmentType; label: string }[] = [
  { value: "contract", label: "Contract" },
  { value: "po", label: "PO" },
  { value: "sow", label: "SOW" },
  { value: "renewal", label: "Renewal" },
  { value: "recurring_obligation", label: "Recurring Obligation" },
  { value: "cloud_commitment", label: "Cloud Commitment" },
  { value: "other", label: "Other" },
];

interface AddCommitmentFormProps {
  ctx: ForecastContext;
  onSaved: (record: ForecastInputRecord, refresh: boolean) => void;
  onCancel: () => void;
}

export function AddCommitmentForm({ ctx, onSaved, onCancel }: AddCommitmentFormProps) {
  const [glAccount, setGlAccount] = useState("");
  const [vendor, setVendor] = useState("");
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [commitmentType, setCommitmentType] = useState<ForecastCommitmentInputCommitmentType>("contract");
  const [owner, setOwner] = useState("");
  const [note, setNote] = useState("");
  const [sourceRef, setSourceRef] = useState("");
  const [scheduleType, setScheduleType] = useState<ScheduleType>("specific_month");
  const [schedules, setSchedules] = useState<ScheduleEntry[]>([]);
  const [overlaps, setOverlaps] = useState<ForecastInputRecord[]>([]);
  const [pendingSaveAnyway, setPendingSaveAnyway] = useState(false);

  const createMutation = useCreateForecastCommitment();

  const handleSubmit = useCallback((saveAnyway = false, withRefresh = false) => {
    if (!description || !amount || !glAccount || schedules.length === 0) return;
    createMutation.mutate({
      data: {
        companyId: ctx.companyId,
        fiscalYear: ctx.fiscalYear,
        departmentCode: ctx.departmentCode,
        glAccount,
        economicKey: economicKey(glAccount, vendor, ctx.fiscalYear, "commit"),
        amount: Number(amount),
        currency: "USD",
        sourceReference: sourceRef || `manual-commitment-${Date.now()}`,
        period: schedules[0]?.period ?? ctx.throughPeriod + 1,
        description,
        vendor: vendor || undefined,
        owner: owner || undefined,
        note: note || undefined,
        commitmentType,
        schedules: schedules.map(s => ({ ...s, sourceReference: s.sourceReference })),
        saveAnyway,
        actor: "ER",
      },
    }, {
      onSuccess: (record) => {
        setOverlaps([]);
        setPendingSaveAnyway(false);
        onSaved(record, withRefresh);
      },
      onError: (err: unknown) => {
        const overlap = err as ForecastInputOverlap;
        if (overlap?.code === "POSSIBLE_OVERLAP") {
          setOverlaps(overlap.overlaps ?? []);
          setPendingSaveAnyway(withRefresh);
        }
      },
    });
  }, [ctx, description, amount, glAccount, vendor, sourceRef, owner, note, commitmentType, schedules, createMutation, onSaved]);

  if (overlaps.length > 0) {
    return (
      <OverlapWarning
        overlaps={overlaps}
        onCancel={() => setOverlaps([])}
        isPending={createMutation.isPending}
        onSaveAnyway={() => handleSubmit(true, pendingSaveAnyway)}
      />
    );
  }

  const canSave = !!(description && amount && glAccount && schedules.length > 0);

  return (
    <div className="fi-form" data-testid="fi-add-commitment-form">
      <FormContextStrip ctx={ctx} />

      <div className="fi-field">
        <label htmlFor="fi-commitment-gl">GL Account</label>
        <input
          id="fi-commitment-gl"
          type="text"
          value={glAccount}
          onChange={e => setGlAccount(e.target.value)}
          placeholder="e.g. 45631"
          data-testid="fi-commitment-gl"
        />
      </div>

      <div className="fi-field">
        <label htmlFor="fi-commitment-vendor">Vendor</label>
        <input
          id="fi-commitment-vendor"
          type="text"
          value={vendor}
          onChange={e => setVendor(e.target.value)}
          placeholder="e.g. Microsoft"
          data-testid="fi-commitment-vendor"
        />
      </div>

      <div className="fi-field">
        <label htmlFor="fi-commitment-description">Description <span className="fi-required" aria-label="required">*</span></label>
        <input
          id="fi-commitment-description"
          type="text"
          value={description}
          onChange={e => setDescription(e.target.value)}
          placeholder="e.g. Enterprise Agreement Renewal"
          data-testid="fi-commitment-description"
        />
      </div>

      <div className="fi-field">
        <label htmlFor="fi-commitment-type">Commitment Type</label>
        <select
          id="fi-commitment-type"
          value={commitmentType}
          onChange={e => setCommitmentType(e.target.value as ForecastCommitmentInputCommitmentType)}
          data-testid="fi-commitment-type"
        >
          {COMMITMENT_TYPES.map(ct => (
            <option key={ct.value} value={ct.value}>{ct.label}</option>
          ))}
        </select>
      </div>

      <div className="fi-field">
        <label htmlFor="fi-commitment-amount">Total Amount ($) <span className="fi-required" aria-label="required">*</span></label>
        <input
          id="fi-commitment-amount"
          type="number"
          min={0}
          value={amount}
          onChange={e => setAmount(e.target.value)}
          placeholder="0"
          data-testid="fi-commitment-amount"
        />
      </div>

      <ScheduleBuilder
        fiscalYear={ctx.fiscalYear}
        throughPeriod={ctx.throughPeriod}
        totalAmount={Number(amount) || 0}
        scheduleType={scheduleType}
        onScheduleTypeChange={setScheduleType}
        schedules={schedules}
        onSchedulesChange={setSchedules}
      />

      {schedules.length === 0 && (
        <div className="fi-field-hint" role="alert">
          A schedule is required. Select a schedule type and period above.
        </div>
      )}

      <div className="fi-field">
        <label htmlFor="fi-commitment-owner">Owner</label>
        <input
          id="fi-commitment-owner"
          type="text"
          value={owner}
          onChange={e => setOwner(e.target.value)}
          placeholder="Optional"
          data-testid="fi-commitment-owner"
        />
      </div>

      <div className="fi-field">
        <label htmlFor="fi-commitment-note">Reference / Note</label>
        <input
          id="fi-commitment-note"
          type="text"
          value={note}
          onChange={e => setNote(e.target.value)}
          placeholder="e.g. EA renewal contract ref"
          data-testid="fi-commitment-note"
        />
      </div>

      <div className="fi-field">
        <label htmlFor="fi-commitment-source-ref">Source Reference</label>
        <input
          id="fi-commitment-source-ref"
          type="text"
          value={sourceRef}
          onChange={e => setSourceRef(e.target.value)}
          placeholder="Optional"
          data-testid="fi-commitment-source-ref"
        />
      </div>

      {createMutation.isError && !(createMutation.error as unknown as ForecastInputOverlap)?.code && <SaveError />}

      <FormActions
        isPending={createMutation.isPending}
        canSave={canSave}
        hasRunId={!!ctx.runId}
        prefix="commitment"
        onCancel={onCancel}
        onSaveOnly={() => handleSubmit(false, false)}
        onSaveRefresh={() => handleSubmit(false, true)}
      />
    </div>
  );
}

// ── Add Planned Spend ─────────────────────────────────────────────────────────

interface AddPlannedSpendFormProps {
  ctx: ForecastContext;
  onSaved: (record: ForecastInputRecord, refresh: boolean) => void;
  onCancel: () => void;
}

export function AddPlannedSpendForm({ ctx, onSaved, onCancel }: AddPlannedSpendFormProps) {
  const [glAccount, setGlAccount] = useState("");
  const [vendor, setVendor] = useState("");
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [project, setProject] = useState("");
  const [owner, setOwner] = useState("");
  const [note, setNote] = useState("");
  const [sourceRef, setSourceRef] = useState("");
  const [scheduleType, setScheduleType] = useState<ScheduleType>("specific_month");
  const [schedules, setSchedules] = useState<ScheduleEntry[]>([]);
  const [overlaps, setOverlaps] = useState<ForecastInputRecord[]>([]);
  const [pendingSaveAnyway, setPendingSaveAnyway] = useState(false);

  const createMutation = useCreateForecastPlannedSpend();

  const handleSubmit = useCallback((saveAnyway = false, withRefresh = false) => {
    if (!description || !amount || !glAccount || schedules.length === 0) return;
    createMutation.mutate({
      data: {
        companyId: ctx.companyId,
        fiscalYear: ctx.fiscalYear,
        departmentCode: ctx.departmentCode,
        glAccount,
        economicKey: economicKey(glAccount, vendor, ctx.fiscalYear, "plan"),
        amount: Number(amount),
        currency: "USD",
        sourceReference: sourceRef || `manual-planned-${Date.now()}`,
        period: schedules[0]?.period ?? ctx.throughPeriod + 1,
        description,
        vendor: vendor || undefined,
        owner: owner || undefined,
        note: project ? `${project}${note ? ` | ${note}` : ""}` : note || undefined,
        approved: true,
        schedules: schedules.map(s => ({ ...s, sourceReference: s.sourceReference })),
        saveAnyway,
        actor: "ER",
      },
    }, {
      onSuccess: (record) => {
        setOverlaps([]);
        setPendingSaveAnyway(false);
        onSaved(record, withRefresh);
      },
      onError: (err: unknown) => {
        const overlap = err as ForecastInputOverlap;
        if (overlap?.code === "POSSIBLE_OVERLAP") {
          setOverlaps(overlap.overlaps ?? []);
          setPendingSaveAnyway(withRefresh);
        }
      },
    });
  }, [ctx, description, amount, glAccount, vendor, sourceRef, owner, note, project, schedules, createMutation, onSaved]);

  if (overlaps.length > 0) {
    return (
      <OverlapWarning
        overlaps={overlaps}
        onCancel={() => setOverlaps([])}
        isPending={createMutation.isPending}
        onSaveAnyway={() => handleSubmit(true, pendingSaveAnyway)}
      />
    );
  }

  const canSave = !!(description && amount && glAccount && schedules.length > 0);

  return (
    <div className="fi-form" data-testid="fi-add-planned-form">
      <FormContextStrip ctx={ctx} />

      <div className="fi-field">
        <label htmlFor="fi-planned-description">Description <span className="fi-required" aria-label="required">*</span></label>
        <input
          id="fi-planned-description"
          type="text"
          value={description}
          onChange={e => setDescription(e.target.value)}
          placeholder="e.g. Security Assessment"
          data-testid="fi-planned-description"
        />
      </div>

      <div className="fi-field">
        <label htmlFor="fi-planned-project">Project / Initiative</label>
        <input
          id="fi-planned-project"
          type="text"
          value={project}
          onChange={e => setProject(e.target.value)}
          placeholder="e.g. Zero Trust Program"
          data-testid="fi-planned-project"
        />
      </div>

      <div className="fi-field">
        <label htmlFor="fi-planned-gl">GL Account <span className="fi-required" aria-label="required">*</span></label>
        <input
          id="fi-planned-gl"
          type="text"
          value={glAccount}
          onChange={e => setGlAccount(e.target.value)}
          placeholder="e.g. 45401 Consulting"
          data-testid="fi-planned-gl"
        />
      </div>

      <div className="fi-field">
        <label htmlFor="fi-planned-amount">Total Amount ($) <span className="fi-required" aria-label="required">*</span></label>
        <input
          id="fi-planned-amount"
          type="number"
          min={0}
          value={amount}
          onChange={e => setAmount(e.target.value)}
          placeholder="0"
          data-testid="fi-planned-amount"
        />
      </div>

      <ScheduleBuilder
        fiscalYear={ctx.fiscalYear}
        throughPeriod={ctx.throughPeriod}
        totalAmount={Number(amount) || 0}
        scheduleType={scheduleType}
        onScheduleTypeChange={setScheduleType}
        schedules={schedules}
        onSchedulesChange={setSchedules}
      />

      {schedules.length === 0 && (
        <div className="fi-field-hint" role="alert">
          A schedule is required. Select a schedule type and period above.
        </div>
      )}

      <div className="fi-field">
        <label htmlFor="fi-planned-vendor">Vendor (if known)</label>
        <input
          id="fi-planned-vendor"
          type="text"
          value={vendor}
          onChange={e => setVendor(e.target.value)}
          placeholder="Optional"
          data-testid="fi-planned-vendor"
        />
      </div>

      <div className="fi-field">
        <label htmlFor="fi-planned-owner">Owner</label>
        <input
          id="fi-planned-owner"
          type="text"
          value={owner}
          onChange={e => setOwner(e.target.value)}
          placeholder="e.g. Security"
          data-testid="fi-planned-owner"
        />
      </div>

      <div className="fi-field">
        <label htmlFor="fi-planned-note">Note</label>
        <input
          id="fi-planned-note"
          type="text"
          value={note}
          onChange={e => setNote(e.target.value)}
          placeholder="Optional"
          data-testid="fi-planned-note"
        />
      </div>

      <div className="fi-field">
        <label htmlFor="fi-planned-source-ref">Source Reference</label>
        <input
          id="fi-planned-source-ref"
          type="text"
          value={sourceRef}
          onChange={e => setSourceRef(e.target.value)}
          placeholder="Optional"
          data-testid="fi-planned-source-ref"
        />
      </div>

      {createMutation.isError && !(createMutation.error as unknown as ForecastInputOverlap)?.code && <SaveError />}

      <FormActions
        isPending={createMutation.isPending}
        canSave={canSave}
        hasRunId={!!ctx.runId}
        prefix="planned"
        onCancel={onCancel}
        onSaveOnly={() => handleSubmit(false, false)}
        onSaveRefresh={() => handleSubmit(false, true)}
      />
    </div>
  );
}
